// 網址觸發登入／註冊彈窗：已登入導頁、標記公告延後、SSR 路徑大小寫修正
// 規格：spec/JT-1784716920_url-modal-trigger-announcement-deferral.md
//
// 為什麼是 global middleware 而不是 layouts/default.vue 的 onMounted：
// 1. 公告延後旗標必須早於任何元件 mount 設定，否則 fetchAnnouncements() 先回時公告會搶跑
// 2. 未知路徑（/promo?modal=register）與大小寫路徑（/Register）在 layout mount 前
//    就已被 pages/[...slug].vue 導走，layout 沒有機會處理
//
// 參數正規化（?modal=register → /register）不在這裡做，見 plugins/url-modal-normalize.client.js：
// middleware 的 navigateTo 會覆蓋待處理的歷史項目，上一頁退不回原始推廣網址
//
// 檔名字母序在 i18n-query.global.js 之後，確保 ?lang= 先完成語系切換再判定
import { useMainStore } from '@/stores/main'
import { toLocaleCodes } from '@/utils/i18nLocales'
import { buildNormalizedRoute, resolveModalIntent } from '@/utils/urlModal'

export default defineNuxtRouteMiddleware((to, from) => {
  const { $i18n } = useNuxtApp()
  const locales = toLocaleCodes($i18n?.locales)

  const { intent, source } = resolveModalIntent({ path: to.path, query: to.query, locales })
  if (!intent) return

  // SSR 只做「路徑大小寫」修正（/Register → /register）：不修就會落到 catch-all 而失效。
  // 這是無效網址的修正而非流程分支，用 replace 才不會在歷史留下一筆錯的大小寫網址。
  if (import.meta.server) {
    const target = buildNormalizedRoute({ path: to.path, query: to.query, locales })
    if (source === 'path' && target) {
      return navigateTo(target, { replace: true })
    }
    return
  }

  // 已登入：忽略參數導向 /main，不開任何彈窗
  const { isLoggedIn } = useAuthToken()
  if (isLoggedIn.value) {
    const localePath = useLocalePath()
    const mainPath = localePath('/main')

    // 全域 middleware 早於 guest.js 執行，等於接手了它的導頁，因此同樣需要 guest.js 的
    // NAVIGATION_DUPLICATED 防護：比對 vue-router 解析後的最後一個 matched route record，
    // 避免 from 與 target 實質為同一路由（trailing slash / locale prefix / alias）仍 push
    const router = useRouter()
    const resolved = router.resolve(mainPath)
    const fromLeaf = from.matched[from.matched.length - 1]
    const resolvedLeaf = resolved.matched[resolved.matched.length - 1]
    if (fromLeaf && resolvedLeaf && (fromLeaf.aliasOf || fromLeaf) === (resolvedLeaf.aliasOf || resolvedLeaf)) {
      return abortNavigation()
    }

    return navigateTo(mainPath)
  }

  // 早於任何元件 mount 標記，公告 watcher 才不會搶在彈窗之前開啟
  useMainStore().setUrlModalPending(intent)
})
